import { createSelector } from 'reselect'
import { getAvailableAccommodationBenefits } from 'luxPlus/selectors/benefits/accommodation'
import { getAvailableCruiseBenefits } from 'luxPlus/selectors/benefits/cruise'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { getShouldShowLuxPlusUpsellForTour } from 'checkout/selectors/tourV2Selectors'
import { getAvailableInsuranceBenefits } from 'luxPlus/selectors/benefits/insurance'
import { getAvailableBookingProtectionBenefits } from 'luxPlus/selectors/benefits/bookingProtection'
import { getCheckoutCruiseOfferView } from 'checkout/selectors/view/cruise'
import { getAvailableExperienceBenefits } from 'luxPlus/selectors/benefits/experience'

export const getCheckoutBenefitsView = createSelector(
  (state: App.State) => getAvailableAccommodationBenefits(state),
  (state: App.State) => getAvailableInsuranceBenefits(state),
  (state: App.State) => getAvailableBookingProtectionBenefits(state),
  (state: App.State) => getAvailableCruiseBenefits(state),
  (state: App.State) => getAvailableExperienceBenefits(state),
  (state: App.State) => state.luxPlus.products.subscription,
  (state: App.State) => getShouldShowLuxPlusUpsellForTour(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => checkoutAccommodationOfferView(state).hasRequiredData && getCheckoutCruiseOfferView(state).hasRequiredData,
  (
    accommodationBenefits,
    insuranceBenefits,
    bookingProtectionBenefits,
    cruiseBenefits,
    experienceBenefits,
    productSubscription,
    showLuxPlusUpsellForTour,
    accommodationOfferView,
    offerViewsHaveRequiredData,
  ): App.WithDataStatus<App.Checkout.LuxPlusCheckoutBenefitsView | undefined> => {
    const {
      membershipRequired,
    } = accommodationBenefits

    // Add future benefits with member price savings here like insurance
    const allBenefitTypes = [accommodationBenefits, cruiseBenefits, insuranceBenefits, bookingProtectionBenefits, experienceBenefits]

    const {
      totalMemberPriceSavings,
      totalPrice,
      totalMemberPrice,
      totalBaseMemberPrice,
    } = allBenefitTypes.reduce((acc, benefit) => ({
      totalMemberPriceSavings: acc.totalMemberPriceSavings + benefit.totalMemberPriceSavings,
      totalPrice: acc.totalPrice + benefit.totalPrice,
      totalMemberPrice: acc.totalMemberPrice + benefit.totalMemberPrice,
      totalBaseMemberPrice: acc.totalBaseMemberPrice + benefit.totalBaseMemberPrice,
    }), {
      totalMemberPriceSavings: 0,
      totalPrice: 0,
      totalMemberPrice: 0,
      totalBaseMemberPrice: 0,
    })

    const inclusions = [...accommodationBenefits.inclusions, ...cruiseBenefits.inclusions]
    const hasRequiredData = offerViewsHaveRequiredData && !!productSubscription.offers?.length && !productSubscription.fetching && !insuranceBenefits.fetching && !bookingProtectionBenefits.fetching

    if (!!productSubscription.error ||
      (!membershipRequired &&
        !cruiseBenefits.hasLuxPlusInclusions &&
        !accommodationBenefits.hasLuxPlusInclusions &&
        totalBaseMemberPrice === 0 &&
        !showLuxPlusUpsellForTour)
    ) {
      return {
        hasRequiredData: hasRequiredData || !!productSubscription.error,
        data: undefined,
      }
    }

    const firstAccommodationOffer = accommodationOfferView.data[0]?.offer as App.Offer | App.Tours.TourV2Offer | undefined

    return {
      data: {
        accommodation: accommodationBenefits,
        insurance: insuranceBenefits,
        bookingProtection: bookingProtectionBenefits,
        cruise: cruiseBenefits,
        combined: {
          totalMemberPriceSavings,
          totalPrice,
          totalBaseMemberPrice,
          totalMemberPrice,
          inclusions,
        },
        durationLabel: accommodationOfferView.data.map(view => view.durationLabel).join(', '),
        offerTitle: accommodationOfferView.data.map(view => view.subLabel ?? view.mainLabel).join(', '),
        firstAccommodationOffer,
      },
      hasRequiredData,
    }
  },
)
